import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Segmented, Table, Tag, Tooltip, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PRIORITY_OPTIONS, TASK_STATUS_META, workV2Api } from '../../../utils/workV2Api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { fmtDate } from './workV2Utils';

const PRIORITY_COLORS = { high: 'red', medium: 'blue', low: 'default' };

/**
 * تعداد وظایف وصل‌شده به یک رکورد (برای عدد کنار تب)؛ null تا رسیدن پاسخ یا اگر کاربر دسترسی ندارد.
 */
export const useLinkedTaskCount = (type, id) => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!type || !id) return undefined;
    let alive = true;
    workV2Api.links
      .tasksFor(type, id, { per_page: 1, status: 'open' })
      .then((res) => alive && setCount(res?.meta?.total ?? 0))
      .catch(() => alive && setCount(null));
    return () => {
      alive = false;
    };
  }, [type, id]);

  return count;
};

/**
 * تب/بخش «وظایف» صفحه‌ی مشتری، سرنخ، معامله، سفارش، تیکت و قرارداد: وظایف وصل‌شده به همین رکورد.
 * recordLabel عنوان رکورد است تا هنگام «وظیفه جدید» از قبل به همین رکورد وصل شود.
 */
const LinkedTasksPanel = ({ type, id, recordLabel, returnTo }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const load = useCallback(
    async (page = 1, pageSize = 10) => {
      setLoading(true);
      try {
        const res = await workV2Api.links.tasksFor(type, id, { status, page, per_page: pageSize });
        setRows(res?.data || []);
        setPagination({ current: res?.meta?.current_page || page, pageSize, total: res?.meta?.total || 0 });
        setForbidden(false);
      } catch (e) {
        if (e?.status === 403 || /403/.test(String(e?.message))) setForbidden(true);
        else message.error('خطا در دریافت وظایف مرتبط');
      } finally {
        setLoading(false);
      }
    },
    [type, id, status],
  );

  useEffect(() => {
    load(1, pagination.pageSize);
    // با عوض شدن رکورد یا فیلتر از صفحه‌ی اول شروع می‌شود
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id, status]);

  const openTask = (task) => navigate(`/work/tasks/${task.id}`, { state: returnTo ? { returnTo } : undefined });

  const create = () =>
    navigate('/work/tasks/add', {
      state: { prefillLinks: [{ type, id: Number(id), label: recordLabel || '' }], returnTo },
    });

  if (forbidden) {
    return <Empty description={<span style={{ fontFamily: 'CustomFont' }}>به وظایف این رکورد دسترسی ندارید</span>} />;
  }

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <Segmented
          value={status}
          onChange={setStatus}
          options={[
            { value: 'open', label: 'باز' },
            { value: 'done', label: 'انجام‌شده' },
            { value: 'all', label: 'همه' },
          ]}
        />
        <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={create}>
          وظیفه جدید
        </Button>
      </div>
      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: <Empty description="وظیفه‌ای وصل نشده است" /> }}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          onChange: (page, pageSize) => load(page, pageSize),
        }}
        scroll={{ x: 640 }}
        columns={[
          {
            title: 'عنوان',
            dataIndex: 'title',
            ellipsis: true,
            render: (t, row) => (
              <button
                type="button"
                className="text-indigo-600 dark:text-indigo-400 font-medium text-right bg-transparent border-0 p-0 cursor-pointer hover:underline"
                style={{ fontFamily: 'CustomFont' }}
                onClick={() => openTask(row)}
              >
                {t}
              </button>
            ),
          },
          {
            title: 'وضعیت',
            dataIndex: 'status',
            width: 130,
            render: (v, row) => {
              const meta = TASK_STATUS_META[v];
              return <Tag color={meta?.color}>{row.board_column?.name || meta?.label || v}</Tag>;
            },
          },
          {
            title: 'اولویت',
            dataIndex: 'priority',
            width: 90,
            render: (v) => <Tag color={PRIORITY_COLORS[v]}>{PRIORITY_OPTIONS.find((p) => p.value === v)?.label || v}</Tag>,
          },
          {
            title: 'مسئول',
            dataIndex: 'assignee',
            width: 150,
            render: (a) => (a?.name ? <Tooltip title={a.name}>{a.name}</Tooltip> : '—'),
          },
          { title: 'سررسید', dataIndex: 'due_date', width: 110, render: (v) => fmtDate(v) },
        ]}
      />
    </div>
  );
};

export default LinkedTasksPanel;
